import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OffersTab = ({ restaurantId }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    offerType: 'PERCENTAGE',
    offerValue: '',
    minOrder: '',
    maxDiscount: '',
    startDate: '',
    endDate: '',
    status: true
  });

  useEffect(() => {
    fetchOffers();
  }, [restaurantId]);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      const filtered = response.data.filter(o => o.resturantId === restaurantId);
      setOffers(filtered);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setCurrentOffer(null);
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      title: '',
      description: '',
      offerType: 'PERCENTAGE',
      offerValue: '',
      minOrder: '',
      maxDiscount: '',
      startDate: today,
      endDate: today,
      status: true
    });
    setShowDialog(true);
  };

  const openEditDialog = (offer) => {
    setCurrentOffer(offer);
    setFormData({
      title: offer.title || '',
      description: offer.description || '',
      offerType: offer.offerType || 'PERCENTAGE',
      offerValue: offer.offerValue || '',
      minOrder: offer.minOrder || '',
      maxDiscount: offer.maxDiscount || '',
      startDate: offer.startDate ? new Date(offer.startDate).toISOString().split('T')[0] : '',
      endDate: offer.endDate ? new Date(offer.endDate).toISOString().split('T')[0] : '',
      status: offer.status !== undefined ? offer.status : true
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        resturantId: restaurantId,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString()
      };

      if (currentOffer) {
        await axios.patch(`${API}/offers/${currentOffer.id}`, submitData);
        toast.success('Offer updated successfully');
      } else {
        await axios.post(`${API}/offers`, submitData);
        toast.success('Offer created successfully');
      }

      setShowDialog(false);
      fetchOffers();
    } catch (error) {
      console.error('Failed to save offer:', error);
      toast.error('Failed to save offer');
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      await axios.delete(`${API}/offers/${offerId}`);
      toast.success('Offer deleted successfully');
      fetchOffers();
    } catch (error) {
      console.error('Failed to delete offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Restaurant Offers ({offers.length})</h2>
        <Button onClick={openCreateDialog} data-testid="create-offer-button" style={{ backgroundColor: '#C2410C' }}>
          <Plus size={20} className="mr-2" />
          Add Offer
        </Button>
      </div>

      {/* Offers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {offers.map((offer) => (
          <Card key={offer.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <Tag size={20} style={{ color: '#C2410C' }} />
                  <CardTitle className="text-xl">{offer.title}</CardTitle>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEditDialog(offer)}>
                    <Edit size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteOffer(offer.id)}>
                    <Trash2 size={16} className="text-red-600" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={offer.status ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                  {offer.status ? 'Active' : 'Inactive'}
                </Badge>
                <Badge variant="outline">
                  {offer.offerType === 'PERCENTAGE' ? `${offer.offerValue}% OFF` : `₹${offer.offerValue} OFF`}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">{offer.description || 'No description'}</p>
              <div className="space-y-2 text-sm">
                {offer.minOrder && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Min Order:</span>
                    <span className="font-medium">₹{offer.minOrder}</span>
                  </div>
                )}
                {offer.maxDiscount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Discount:</span>
                    <span className="font-medium">₹{offer.maxDiscount}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Valid Till:</span>
                  <span className="font-medium">{formatDate(offer.endDate)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {offers.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No offers found. Create your first offer!
        </div>
      )}

      {/* Offer Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{currentOffer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Offer Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., 20% OFF on all orders"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Offer details..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Offer Type *</Label>
                <Select value={formData.offerType} onValueChange={(value) => setFormData({ ...formData, offerType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                    <SelectItem value="FLAT">Flat Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Offer Value *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.offerValue}
                  onChange={(e) => setFormData({ ...formData, offerValue: e.target.value })}
                  placeholder={formData.offerType === 'PERCENTAGE' ? '20' : '100'}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Min Order Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.minOrder}
                  onChange={(e) => setFormData({ ...formData, minOrder: e.target.value })}
                />
              </div>

              <div>
                <Label>Max Discount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.maxDiscount}
                  onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                />
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
              />
              <span>Active</span>
            </label>

            <Button type="submit" className="w-full" style={{ backgroundColor: '#C2410C' }}>
              {currentOffer ? 'Update Offer' : 'Create Offer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OffersTab;
